import { useEffect, useRef, useCallback } from 'react';

// Configure allowed origins for postMessage security
const ALLOWED_ORIGINS = [
    window.location.origin,
    'https://outlookweb.linq.legal',
    'https://localhost:3000'    
];

const useWindowEventHandlers = () => {
    const handlersRef = useRef({});
    const messageHandlersRef = useRef({});

    const registerHandler = useCallback((eventName, handler) => {
        if (handlersRef.current[eventName]) {
            delete window[handlersRef.current[eventName]];
        }

        window[eventName] = handler;
        handlersRef.current[eventName] = handler;

        return () => {
            delete window[eventName];
            delete handlersRef.current[eventName];
        };
    }, []);

    const unregisterHandler = useCallback((eventName) => {
        if (handlersRef.current[eventName]) {
            delete window[eventName];
            delete handlersRef.current[eventName];
        }
    }, []);

    const registerMessageHandler = useCallback((action, handler) => {
        messageHandlersRef.current[action] = handler;

        return () => {
            delete messageHandlersRef.current[action];
        };
    }, []);

    const unregisterMessageHandler = useCallback((action) => {
        delete messageHandlersRef.current[action];
    }, []);

    useEffect(() => {
        const onMessage = (event) => {
            // SECURITY: Origin validation is sufficient when app requires login
            if (!ALLOWED_ORIGINS.includes(event.origin)) {
                console.warn(`[useEventDispatcher] Blocked message from untrusted origin: ${event.origin}`);
                return;
            }

            const data = event.data;
            if (!data || typeof data !== "object") return;

            const { action, payload } = data;
            if (!action || typeof action !== "string") return;

            const handler = messageHandlersRef.current[action];
            if (typeof handler === "function") {
                try {
                    handler(payload, event);
                } catch (error) {
                    console.error(`[useEventDispatcher] Error in handler for action "${action}":`, error);
                }
            }
        };

        window.addEventListener('message', onMessage);

        return () => {
            Object.keys(handlersRef.current).forEach((eventName) => {
                delete window[eventName];
            });
            handlersRef.current = {};
            messageHandlersRef.current = {};
            window.removeEventListener('message', onMessage);
        };
    }, []);

    return {
        registerHandler,
        unregisterHandler,
        registerMessageHandler,
        unregisterMessageHandler,
    };
};

export default useWindowEventHandlers;
